import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import ContractLayout from "../components/ContractLayout";
import SEOHead from "../components/SEOHead";
import useIsMobile from "../hooks/useIsMobile";
import BLOG_ARTICLES from "./articles";
import {
  buildArticleSchema,
  buildBreadcrumbSchema,
  buildFAQSchemaFromBlogSections,
  injectSchemas,
} from "../utils/structuredData";
import { trackBlogCtaClick } from "../utils/analytics";
import { getContractIdFromSlug } from "../utils/contractSlugs";

// Parse [text](/url) markdown links in strings
function renderTextWithLinks(text) {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) => {
    const match = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (match) {
      return (
        <Link key={i} to={match[2]} style={{ color: "#c45d3e", fontWeight: 600, textDecoration: "none" }}>
          {match[1]}
        </Link>
      );
    }
    return part;
  });
}

export default function BlogArticlePage() {
  const { slug } = useParams();
  const isMobile = useIsMobile();

  const article = BLOG_ARTICLES.find((a) => a.slug === slug);

  // JSON-LD schema injection
  useEffect(() => {
    if (!article) return;
    const articleUrl = `https://generator-contracte.com/blog/${article.slug}`;
    return injectSchemas(
      [
        buildArticleSchema({
          title: article.title,
          description: article.description,
          date: article.date,
          url: articleUrl,
        }),
        buildBreadcrumbSchema([
          { name: "Acasa", item: "https://generator-contracte.com/" },
          { name: "Blog", item: "https://generator-contracte.com/blog" },
          { name: article.title, item: articleUrl },
        ]),
        buildFAQSchemaFromBlogSections(article.sections),
      ],
      "blog-schema"
    );
  }, [article]);

  if (!article) {
    return (
      <div style={{ textAlign: "center", padding: "80px 24px", fontFamily: "'DM Sans', sans-serif" }}>
        <h1>Articol negasit</h1>
        <p>Acest articol nu exista.</p>
        <Link to="/blog" style={{ color: "#c45d3e", fontWeight: 600 }}>Inapoi la blog</Link>
      </div>
    );
  }

  const relatedArticles = (article.relatedSlugs || [])
    .map((s) => BLOG_ARTICLES.find((a) => a.slug === s))
    .filter(Boolean);

  return (
    <>
      <SEOHead
        title={article.seoTitle}
        description={article.description}
        canonical={`https://generator-contracte.com/blog/${article.slug}`}
        ogType="article"
      />
      <ContractLayout
        title="Blog"
        subtitle="Ghiduri practice si informatii utile despre contracte si legislatie"
      >
        <div style={{ maxWidth: "800px", margin: "0 auto", padding: "24px 24px 0" }}>
          {/* Breadcrumbs */}
          <nav style={{ fontSize: "13px", color: "#8a7e70", marginBottom: "16px" }}>
            <Link to="/" style={{ color: "#8a7e70", textDecoration: "none" }}>Acasa</Link>
            <span style={{ margin: "0 8px" }}>›</span>
            <Link to="/blog" style={{ color: "#8a7e70", textDecoration: "none" }}>Blog</Link>
            <span style={{ margin: "0 8px" }}>›</span>
            <span style={{ color: "#4a3f35" }}>{article.title}</span>
          </nav>

          <article style={{
            backgroundColor: "#fff",
            borderRadius: "16px",
            padding: isMobile ? "32px 20px" : "48px 40px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
            border: "1px solid #e8e0d4",
          }}>
            {/* Article header */}
            <div style={{ marginBottom: "32px" }}>
              <h1 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(24px, 4vw, 32px)",
                fontWeight: 800,
                color: "#2d2418",
                margin: "0 0 12px 0",
                lineHeight: 1.3,
              }}>
                {article.title}
              </h1>
              <div style={{
                fontSize: "13px",
                color: "#8a7e70",
                fontWeight: 500,
              }}>
                {article.date} · {article.readTime} citire
              </div>
            </div>

            {/* Sections */}
            {article.sections.map((section, i) => (
              <div key={i} style={{ marginBottom: i < article.sections.length - 1 ? "28px" : "0" }}>
                {section.title && (
                  <h2 style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: "22px",
                    fontWeight: 700,
                    color: "#2d2418",
                    margin: "0 0 12px 0",
                  }}>
                    {section.title}
                  </h2>
                )}
                {Array.isArray(section.content) ? (
                  <ul style={{
                    margin: 0,
                    paddingLeft: "20px",
                    listStyleType: "disc",
                  }}>
                    {section.content.map((item, j) => (
                      <li key={j} style={{
                        fontSize: "14px",
                        color: "#6b6156",
                        lineHeight: 1.7,
                        marginBottom: "4px",
                      }}>
                        {renderTextWithLinks(item)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{
                    fontSize: "14px",
                    color: "#6b6156",
                    margin: 0,
                    lineHeight: 1.7,
                  }}>
                    {renderTextWithLinks(section.content)}
                  </p>
                )}
              </div>
            ))}

            {/* CTA banner */}
            {article.cta && (
              <div style={{
                marginTop: "40px",
                padding: isMobile ? "24px 20px" : "32px",
                backgroundColor: "rgba(196, 93, 62, 0.06)",
                borderRadius: "12px",
                border: "1px solid rgba(196, 93, 62, 0.12)",
                textAlign: "center",
              }}>
                <p style={{
                  fontSize: "15px",
                  color: "#4a3f35",
                  margin: "0 0 20px 0",
                  lineHeight: 1.6,
                }}>
                  {article.cta.text}
                </p>
                {(article.cta.buttons || [{ text: article.cta.buttonText, link: article.cta.link }]).map((btn, i) => (
                  <Link
                    key={i}
                    to={btn.link}
                    style={{
                      display: "inline-block",
                      padding: "14px 32px",
                      margin: "0 8px 8px",
                      background: "linear-gradient(135deg, #c45d3e 0%, #a84832 100%)",
                      color: "#fff",
                      borderRadius: "10px",
                      fontSize: "15px",
                      fontWeight: 700,
                      fontFamily: "'DM Sans', sans-serif",
                      textDecoration: "none",
                      boxShadow: "0 4px 14px rgba(196,93,62,0.3)",
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => {
                      window.scrollTo(0, 0);
                      // Report the contract id, not the route slug, so
                      // contract_type shares one vocabulary across all events.
                      trackBlogCtaClick(article.slug, getContractIdFromSlug(btn.link));
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = "0 6px 20px rgba(196,93,62,0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 14px rgba(196,93,62,0.3)";
                    }}
                  >
                    {btn.text}
                  </Link>
                ))}
              </div>
            )}
          </article>
        </div>

        {/* Related articles */}
        {relatedArticles.length > 0 && (
          <div style={{ maxWidth: "800px", margin: "0 auto", padding: "24px 24px 0" }}>
            <h2 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "20px",
              fontWeight: 700,
              color: "#2d2418",
              margin: "0 0 16px 0",
            }}>
              Articole similare
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/blog/${rel.slug}`}
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    padding: isMobile ? "16px" : "20px 24px",
                    border: "1px solid #e8e0d4",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                    textDecoration: "none",
                    color: "inherit",
                    display: "flex",
                    alignItems: isMobile ? "flex-start" : "center",
                    gap: "16px",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.03)";
                  }}
                >
                  <div style={{ fontSize: "28px", flexShrink: 0 }}>{rel.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: "16px",
                      fontWeight: 700,
                      color: "#2d2418",
                      margin: "0 0 4px 0",
                    }}>
                      {rel.title}
                    </h3>
                    <span style={{ fontSize: "12px", color: "#8a7e70" }}>
                      {rel.readTime} citire
                    </span>
                  </div>
                  <span style={{ color: "#c45d3e", fontWeight: 600, fontSize: "14px", flexShrink: 0 }}>→</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div style={{ maxWidth: "800px", margin: "0 auto", padding: "24px 24px 40px" }}>
          <div style={{
            padding: "20px 24px",
            backgroundColor: "rgba(196, 93, 62, 0.06)",
            borderRadius: "12px",
            border: "1px solid rgba(196, 93, 62, 0.12)",
          }}>
            <p style={{ fontSize: "13px", color: "#8a7e70", margin: 0, lineHeight: 1.6 }}>
              <strong style={{ color: "#4a3f35" }}>Disclaimer:</strong> Informatiile din acest articol au caracter orientativ si nu constituie consultanta juridica sau fiscala. Pentru situatii particulare, se recomanda consultarea unui avocat sau consultant fiscal.
            </p>
          </div>
        </div>
      </ContractLayout>
    </>
  );
}
